import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { ErrorState } from '../ui/QueryStates';
import { dash } from '@/components/ui/data-table';
import { Field, field } from '../create/Field';
import { IconCustomers } from '../icons';
import { EditSheet, DangerAction, changedFields, toField } from '../ui/EditSheet';

/**
 * The people at a customer (PRD-v1 data model: "one `customers` table + a `contacts` child
 * table; commercial reality = two pointers"). The card used to render the contacts that
 * came back with the customer and say "No contacts yet." with no way to add one — so a
 * commercial account could never get the second name it needs.
 *
 * It owns its own query rather than reading the parent's, so adding a contact refreshes
 * this card instead of the whole page — and the card works anywhere a customer id exists.
 */
export function ContactsCard({ customerId }: { customerId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  /** Which person is open for editing — a list, so an id rather than a flag. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [roleLabel, setRoleLabel] = useState('');

  const q = useQuery(trpc.customer.contacts.queryOptions({ customerId }));
  const add = useMutation(
    trpc.customer.addContact.mutationOptions({
      onSuccess: () => {
        setAdding(false);
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setRoleLabel('');
        void qc.invalidateQueries();
      },
    }),
  );

  const contacts = q.data ?? [];
  const noPrimaryYet = contacts.length === 0;
  const editingContact = contacts.find((c) => c.id === editingId);

  return (
    <SectionCard
      title={`Contacts (${contacts.length})`}
      right={
        adding ? null : <Button onClick={() => setAdding(true)}>Add a contact</Button>
      }
    >
      {add.error ? <Notice tone="danger">{add.error.message}</Notice> : null}

      {adding ? (
        <div className="mb-3 space-y-2 rounded-[var(--radius)] border border-border p-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name">
              <input
                className={field}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
              />
            </Field>
            <Field label="Last name">
              <input
                className={field}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
            <Field label="Email">
              <input
                className={field}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputMode="email"
              />
            </Field>
            <Field label="Phone">
              <input
                className={field}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
              />
            </Field>
          </div>
          <Field label="What they do">
            <input
              className={field}
              value={roleLabel}
              onChange={(e) => setRoleLabel(e.target.value)}
              placeholder="Property manager, accounts payable, site foreman…"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={!firstName.trim() || add.isPending}
              onClick={() =>
                add.mutate({
                  customerId,
                  firstName: firstName.trim(),
                  lastName: lastName.trim() || undefined,
                  email: email.trim() || undefined,
                  phone: phone.trim() || undefined,
                  roleLabel: roleLabel.trim() || undefined,
                  // The first person on an account is the one you call. Making somebody
                  // tick a box to say so is a step that exists only to be forgotten.
                  isPrimary: noPrimaryYet,
                  ...idem(),
                })
              }
            >
              {add.isPending ? 'Saving…' : 'Save contact'}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Never mind
            </Button>
            {noPrimaryYet ? (
              <span className="text-xs text-muted-foreground">
                This one becomes the main contact.
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {q.isPending ? (
        <Skeleton className="h-16" />
      ) : q.isError ? (
        <ErrorState
          icon={IconCustomers}
          what="contacts"
          onRetry={() => void q.refetch()}
        />
      ) : contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nobody added yet. On a commercial account this is where the property manager and
          whoever pays the bills live.
        </p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {contacts.map((c) => (
            <li
              key={c.id}
              className="flex items-baseline justify-between gap-3 py-2 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {c.firstName} {c.lastName ?? ''}
                  {c.isPrimary ? (
                    <span className="badge badge--brand ml-2">main</span>
                  ) : null}
                </p>
                {c.roleLabel ? (
                  <p className="text-xs text-muted-foreground">{c.roleLabel}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="text-right text-muted-foreground">
                  <p>{dash(c.email)}</p>
                  <p>{dash(c.phone)}</p>
                </div>
                <Button variant="ghost" onClick={() => setEditingId(c.id)}>
                  Edit
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Only the whole row prefills the edit: a tech's answer holds back the email (CRM 008),
          which would save as cleared. The check is on the email itself. */}
      {editingContact && editingContact.email !== undefined ? (
        <EditContactSheet
          key={editingContact.id}
          contact={editingContact}
          onClose={() => setEditingId(null)}
        />
      ) : null}
    </SectionCard>
  );
}

/**
 * Fix a person's details, or take them off the account.
 *
 * Lives here rather than on its own page because a contact only ever makes sense next to
 * its customer — and because the card already owned adding one, so editing belongs beside it.
 */
function EditContactSheet({
  contact,
  onClose,
}: {
  contact: {
    id: string;
    firstName: string;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    roleLabel: string | null;
    isPrimary: boolean;
  };
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();

  const original = {
    firstName: contact.firstName,
    lastName: toField(contact.lastName),
    email: toField(contact.email),
    phone: toField(contact.phone),
    roleLabel: toField(contact.roleLabel),
  };
  const [draft, setDraft] = useState(original);
  const [primary, setPrimary] = useState(contact.isPrimary);
  const set = (k: keyof typeof original) => (v: string) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const changed = changedFields(original, draft);
  const dirty = Object.keys(changed).length > 0 || primary !== contact.isPrimary;

  const done = () => {
    void qc.invalidateQueries();
    onClose();
  };
  const save = useMutation(
    trpc.customer.updateContact.mutationOptions({ onSuccess: done }),
  );
  const archive = useMutation(
    trpc.customer.archiveContact.mutationOptions({ onSuccess: done }),
  );

  return (
    <EditSheet
      open
      onClose={onClose}
      title={`Edit ${contact.firstName}`}
      dirty={dirty}
      saving={save.isPending}
      error={save.error ?? archive.error}
      onSave={() =>
        save.mutate({
          contactId: contact.id,
          ...changed,
          // Only sent when it actually moved — otherwise every save would re-assert
          // primary and demote whoever else holds it.
          ...(primary !== contact.isPrimary ? { isPrimary: primary } : {}),
          ...idem(),
        })
      }
      destructive={
        <DangerAction
          label="Remove from this customer"
          question={`Remove ${contact.firstName}?`}
          detail="They stop showing on the account. Notes and jobs that mention them are untouched."
          pending={archive.isPending}
          onConfirm={() => archive.mutate({ id: contact.id, ...idem() })}
        />
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <Field label="First name">
          <input
            className={field}
            value={draft.firstName}
            onChange={(e) => set('firstName')(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Last name">
          <input
            className={field}
            value={draft.lastName}
            onChange={(e) => set('lastName')(e.target.value)}
          />
        </Field>
        <Field label="Email">
          <input
            className={field}
            value={draft.email}
            onChange={(e) => set('email')(e.target.value)}
            inputMode="email"
          />
        </Field>
        <Field label="Phone">
          <input
            className={field}
            value={draft.phone}
            onChange={(e) => set('phone')(e.target.value)}
            inputMode="tel"
          />
        </Field>
      </div>
      <Field label="What they do">
        <input
          className={field}
          value={draft.roleLabel}
          onChange={(e) => set('roleLabel')(e.target.value)}
          placeholder="Property manager, accounts payable, site foreman…"
        />
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={primary}
          disabled={contact.isPrimary}
          onChange={(e) => setPrimary(e.target.checked)}
        />
        <span>
          Main contact
          {contact.isPrimary ? (
            <span className="ml-2 text-xs text-muted-foreground">
              — promote somebody else to move this
            </span>
          ) : null}
        </span>
      </label>
    </EditSheet>
  );
}
